'use client'

import { Star } from 'lucide-react'
import { Card, CardContent } from '@/components/ui/Card'
import { useHomepageReviews } from '@/services/surveys'
import type { HomepageReview } from '@/services/surveys'
import { cn } from '@/lib/utils'

const REVIEW_LIMIT = 12

function formatReviewDate(createdAt: string): string {
  return new Intl.DateTimeFormat(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  }).format(new Date(createdAt))
}

function StarRating({ rating }: { rating: number }) {
  const normalizedRating = Math.max(0, Math.min(5, Math.round(rating)))

  return (
    <div
      className="flex items-center gap-1 text-amber-400"
      aria-label={`${normalizedRating} out of 5 stars`}
    >
      {Array.from({ length: 5 }).map((_, index) => {
        const isFilled = index < normalizedRating

        return (
          <Star
            key={index}
            className={cn('h-4 w-4', isFilled && 'fill-current')}
            aria-hidden="true"
          />
        )
      })}
    </div>
  )
}

function ReviewCard({ review }: { review: HomepageReview }) {
  return (
    <Card
      className={cn(
        'relative h-full overflow-hidden border-border/70 bg-card/95 shadow-sm transition-shadow hover:shadow-md',
        review.isFeatured && 'border-primary/30 shadow-md'
      )}
    >
      <CardContent className="flex h-full flex-col gap-5 p-6">
        <div className="pointer-events-none absolute -top-5 left-4 text-7xl font-serif leading-none text-primary/10">
          &ldquo;
        </div>

        <div className="relative flex items-start justify-between gap-4">
          {review.rating ? <StarRating rating={review.rating} /> : <span />}
          {review.isFeatured && (
            <span className="rounded-full border border-primary/20 bg-primary/10 px-2 py-0.5 text-xs font-medium text-primary">
              Featured
            </span>
          )}
        </div>

        <div className="relative flex flex-1 flex-col gap-3">
          {review.title && (
            <h3 className="text-base font-semibold text-heading">
              {review.title}
            </h3>
          )}
          {review.body && (
            <p className="line-clamp-4 text-sm leading-6 text-muted-foreground">
              {review.body}
            </p>
          )}
        </div>

        <div className="relative mt-auto flex items-center justify-between gap-4 border-t border-border/60 pt-4 text-xs text-muted-foreground">
          <span className="font-medium text-foreground">
            {review.displayName}
          </span>
          <time dateTime={review.createdAt}>
            {formatReviewDate(review.createdAt)}
          </time>
        </div>
      </CardContent>
    </Card>
  )
}

export default function TestimonialsSection() {
  const { data: reviewsData } = useHomepageReviews(REVIEW_LIMIT)

  if (!reviewsData || reviewsData.reviews.length === 0) {
    return null
  }

  return (
    <section className="bg-background px-4 py-16 sm:px-6 md:py-24 lg:px-8">
      <div className="mx-auto max-w-7xl">
        <div className="mx-auto mb-10 max-w-3xl text-center md:mb-14">
          <p className="mb-3 text-sm font-semibold uppercase tracking-[0.2em] text-primary">
            Testimonials
          </p>
          <h2 className="text-2xl font-bold text-heading sm:text-3xl md:text-4xl">
            What Our Customers Say
          </h2>
        </div>

        <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
          {reviewsData.reviews.map((review) => (
            <ReviewCard key={review.id} review={review} />
          ))}
        </div>
      </div>
    </section>
  )
}
